import * as PIXI from 'pixi.js';
import { Vector2 } from '../utils/Vector2';
import { Enemy } from '../types/GameTypes';
import { GraphicsPool } from '../utils/ObjectPool';

/**
 * GPU-accelerated renderer for game entities
 * Uses ParticleContainer for instanced rendering and object pooling
 */
export class GPURenderer {
  // Particle containers for GPU batch rendering
  private enemyContainer: PIXI.ParticleContainer;
  private projectileContainer: PIXI.ParticleContainer;
  
  // Object pools to avoid garbage collection
  private graphicsPool: GraphicsPool;
  
  // Texture cache for sprites
  private textureCache: Map<string, PIXI.Texture> = new Map();
  
  // Active graphics objects (for pooling)
  private activeGraphics: PIXI.Graphics[] = [];
  
  constructor() {
    // Create particle containers with optimal settings
    // ParticleContainer uses GPU instancing for massive performance boost
    this.enemyContainer = new PIXI.ParticleContainer(1000, {
      scale: true,
      position: true,
      rotation: false,
      uvs: false,
      tint: true,
    });
    
    this.projectileContainer = new PIXI.ParticleContainer(500, {
      scale: true,
      position: true,
      rotation: false,
      uvs: false,
      tint: true,
    });
    
    // Initialize object pool
    this.graphicsPool = new GraphicsPool(100, 1000);
    
    console.log('[GPU] Renderer initialized with particle containers');
  }

  /**
   * Create a canvas texture for a circle (enemy or projectile)
   */
  private createCircleTexture(radius: number, color: number): PIXI.Texture {
    const size = radius * 2 + 2; // Add padding
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    // Draw circle
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.beginPath();
    ctx.arc(radius + 1, radius + 1, radius, 0, Math.PI * 2);
    ctx.fill();
    
    return PIXI.Texture.from(canvas);
  }

  /**
   * Get or create enemy texture (circle with color)
   */
  private getEnemyTexture(color: number): PIXI.Texture {
    const key = `enemy_${color.toString(16)}`;
    if (!this.textureCache.has(key)) {
      this.textureCache.set(key, this.createCircleTexture(8, color));
    }
    return this.textureCache.get(key)!;
  }

  /**
   * Get or create projectile texture (circle with color)
   */
  private getProjectileTexture(color: number): PIXI.Texture {
    const key = `projectile_${color.toString(16)}`;
    if (!this.textureCache.has(key)) {
      this.textureCache.set(key, this.createCircleTexture(6, color));
    }
    return this.textureCache.get(key)!;
  }

  /**
   * Render enemies using GPU-accelerated particle container
   */
  renderEnemies(enemies: Enemy[]): void {
    // Clear previous frame
    this.enemyContainer.removeChildren();
    
    // Render each enemy as a sprite
    for (const enemy of enemies) {
      // Determine color
      let color = 0xffaa00;
      if (enemy.type === 'runner') color = 0x00ff00;
      else if (enemy.type === 'armored') color = 0x666666;
      else if (enemy.type === 'ghost') color = 0x00ffff;
      else if (enemy.type === 'flying') color = 0xff00ff;
      else if (enemy.isElite) color = 0xff0000;
      
      // Get texture from cache
      const texture = this.getEnemyTexture(color);
      const sprite = new PIXI.Sprite(texture);
      
      // Position sprite (texture is 18x18 with 8px radius centered at 9,9, so offset by -9)
      sprite.x = enemy.position.x - 9;
      sprite.y = enemy.position.y - 9;
      
      this.enemyContainer.addChild(sprite);
    }
  }

  /**
   * Render projectiles using GPU-accelerated particle container
   */
  renderProjectiles(projectiles: Array<{position: Vector2; color: number}>): void {
    // Clear previous frame
    this.projectileContainer.removeChildren();
    
    // Render each projectile as a sprite
    for (const proj of projectiles) {
      const texture = this.getProjectileTexture(proj.color);
      const sprite = new PIXI.Sprite(texture);
      
      // Position sprite (texture is 14x14 with 6px radius centered at 7,7, so offset by -7)
      sprite.x = proj.position.x - 7;
      sprite.y = proj.position.y - 7;
      
      this.projectileContainer.addChild(sprite);
    }
  }

  /**
   * Acquire a graphics object from the pool
   */
  acquireGraphics(): PIXI.Graphics {
    const graphics = this.graphicsPool.acquire();
    this.activeGraphics.push(graphics);
    return graphics;
  }

  /**
   * Release all active graphics back to pool
   */
  releaseAll(): void {
    for (const graphics of this.activeGraphics) {
      this.graphicsPool.release(graphics);
    }
    this.activeGraphics = [];
  }

  /**
   * Get enemy container
   */
  getEnemyContainer(): PIXI.Container {
    return this.enemyContainer;
  }

  /**
   * Get projectile container
   */
  getProjectileContainer(): PIXI.Container {
    return this.projectileContainer;
  }

  /**
   * Get pool statistics
   */
  getStats(): { graphicsPoolSize: number; texturesCached: number } {
    return {
      graphicsPoolSize: this.graphicsPool.getSize(),
      texturesCached: this.textureCache.size,
    };
  }

  /**
   * Destroy renderer and clean up resources
   */
  destroy(): void {
    this.enemyContainer.destroy({ children: true });
    this.projectileContainer.destroy({ children: true });
    this.graphicsPool.clear();
    // Clean up textures
    for (const texture of this.textureCache.values()) {
      texture.destroy(true);
    }
    this.textureCache.clear();
  }
}
